import { APPEARANCES, sourceOutfitFrame } from "@ninja/asset-catalog";
import { ENTITY_DIRECTION } from "@ninja/contracts";
import { renderBrandIcon } from "./BrandIcon.js";
import { escapeHtml } from "./escapeHtml.js";

export interface NinjaOutfitImageOptions {
  className?: string;
  direction?: number;
  loading?: "eager" | "lazy";
}

const FRONT_DIRECTION = ENTITY_DIRECTION.south;

export function renderNinjaOutfitImage(
  appearanceId: string,
  alt: string,
  options: NinjaOutfitImageOptions = {},
): string {
  const appearance = APPEARANCES[appearanceId];
  if (!appearance) return renderFallback(alt, options.className);

  const preferredDirection = options.direction ?? FRONT_DIRECTION;
  const direction = appearance.sourceDirections.includes(preferredDirection)
    ? preferredDirection
    : (appearance.sourceDirections[0] ?? FRONT_DIRECTION);
  const frame = appearance.sourceFrames[0] ?? 0;
  const src = sourceOutfitFrame(appearance, frame, direction);
  const className = options.className
    ? ` class="${escapeHtml(options.className)}"`
    : "";

  return `<img${className} src="${escapeHtml(src)}" alt="${escapeHtml(alt)}" loading="${options.loading ?? "lazy"}" draggable="false" data-ninja-outfit />`;
}

export function renderNinjaOutfitPortrait(
  appearanceId: string,
  alt: string,
  wrapperClassName = "portrait",
): string {
  const appearance = APPEARANCES[appearanceId];
  const className = escapeHtml(wrapperClassName);
  const accessibleLabel = escapeHtml(alt);

  if (!appearance) {
    return `<div class="${className} portrait-fallback" role="img" aria-label="${accessibleLabel}">${renderBrandIcon({ size: "large" })}</div>`;
  }

  return `<div class="${className}" role="img" aria-label="${accessibleLabel}">${renderNinjaOutfitImage(appearanceId, "", { className: "ninja-outfit-image" })}</div>`;
}

export function bindNinjaOutfitImageFallbacks(root: Document): void {
  root.addEventListener(
    "error",
    (event) => {
      const image = event.target;
      if (
        !(image instanceof HTMLImageElement) ||
        !image.hasAttribute("data-ninja-outfit")
      )
        return;

      const fallback = document.createElement("span");
      fallback.className = "ninja-outfit-image-fallback";
      fallback.setAttribute("role", "img");
      const accessibleLabel =
        image.alt ||
        image
          .closest<HTMLElement>("[aria-label]")
          ?.getAttribute("aria-label") ||
        "Ninja portrait unavailable";
      fallback.setAttribute("aria-label", accessibleLabel);
      fallback.innerHTML = renderBrandIcon({ size: "medium" });
      image.replaceWith(fallback);
    },
    true,
  );
}

function renderFallback(alt: string, className?: string): string {
  const classes = ["ninja-outfit-image-fallback", className]
    .filter(Boolean)
    .map((value) => escapeHtml(value!))
    .join(" ");
  return `<span class="${classes}" role="img" aria-label="${escapeHtml(alt)}">${renderBrandIcon({ size: "medium" })}</span>`;
}
